/**
 * Create Invoice Modal
 * Modal for creating invoices with flexible billing options
 * Supports: Time-based, Flat fee, Project-based, Custom line items
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { useTimeStore } from '../store/timeStore';
import { useClientStore } from '../store/clientStore';
import { createManualInvoice, generateInvoice, createRetainerInvoice } from '../services/invoiceService';
import { formatCurrency, formatHours, formatDate } from '../utils/formatters';
import {
  calculateGraduatedDiscount,
  calculateRetainerAmount,
  getBillingPeriod,
  formatBillingPeriod,
  calculateDiscountSavings,
  getScheduleDisplayInfo,
} from '../utils/graduatedDiscount';
import '../components/Modal.css';
import './InvoiceModal.css';

const CreateInvoiceModal = ({ onClose, onSuccess }) => {
  const user = useAuthStore((state) => state.user);
  const timeEntries = useTimeStore((state) => state.timeEntries);
  const clients = useClientStore((state) => state.clients);

  const [billingType, setBillingType] = useState('manual'); // 'manual', 'time-based', 'retainer'
  const [selectedClient, setSelectedClient] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Client billing config state
  const [discountPercent, setDiscountPercent] = useState(0);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);

  // Time-based billing state
  const [selectedEntries, setSelectedEntries] = useState([]);

  // Manual billing state - line items
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, rate: 0, amount: 0 }
  ]);

  // Retainer billing state
  const [retainerBillingPeriod, setRetainerBillingPeriod] = useState(getBillingPeriod());

  // Set default due date (30 days from now)
  useEffect(() => {
    const defaultDueDate = new Date();
    defaultDueDate.setDate(defaultDueDate.getDate() + 30);
    setDueDate(defaultDueDate.toISOString().split('T')[0]);
  }, []);

  // Auto-populate billing settings when client is selected
  useEffect(() => {
    if (selectedClientData) {
      // Set currency from client or default
      if (selectedClientData.currency) {
        setCurrency(selectedClientData.currency);
      }

      // Set discount from client's billing config
      const clientDiscount = selectedClientData.billingConfig?.discountPercent ||
                            selectedClientData.discountPercent || 0;
      setDiscountPercent(clientDiscount);

      // Check if payment options are enabled
      const paymentOptsEnabled = selectedClientData.billingConfig?.paymentOptionsEnabled || false;
      setShowPaymentOptions(paymentOptsEnabled);
    }
  }, [selectedClientData]);

  // Get unbilled entries for selected client
  const unbilledEntries = timeEntries.filter(
    e => !e.invoiced && !e.isRunning && (!selectedClient || e.clientId === selectedClient)
  );

  // Calculate totals for time-based
  const selectedEntriesData = unbilledEntries.filter(e => selectedEntries.includes(e.id));
  const timeTotalHours = selectedEntriesData.reduce((sum, e) => sum + (e.duration || 0), 0) / 3600;
  const timeTotalAmount = selectedEntriesData.reduce((sum, e) => sum + (e.amount || 0), 0);

  // Calculate totals for manual
  const manualSubtotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const manualDiscountAmount = discountPercent > 0 ? manualSubtotal * (discountPercent / 100) : 0;
  const manualTotal = manualSubtotal - manualDiscountAmount;

  // Get selected client data for retainer calculations
  const selectedClientData = useMemo(() => {
    return clients.find(c => c.id === selectedClient);
  }, [clients, selectedClient]);

  // Calculate retainer pricing with graduated discount
  const retainerInfo = useMemo(() => {
    if (!selectedClientData) {
      return {
        baseRetainer: 0,
        discountInfo: { enabled: false },
        finalAmount: 0,
        discountSavings: null,
        scheduleDisplay: [],
      };
    }

    const baseRetainer = selectedClientData.monthlyRetainer || 0;
    const discountInfo = calculateGraduatedDiscount(selectedClientData.graduatedDiscount);
    const discountSavings = calculateDiscountSavings(baseRetainer, discountInfo.discountPercent);
    const scheduleDisplay = getScheduleDisplayInfo(discountInfo.schedule, discountInfo.currentYear);

    return {
      baseRetainer,
      discountInfo,
      finalAmount: discountSavings.finalAmount,
      discountSavings,
      scheduleDisplay,
    };
  }, [selectedClientData]);

  const handleAddLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const handleRemoveLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;

    // Auto-calculate amount if quantity or rate changes
    if (field === 'quantity' || field === 'rate') {
      const quantity = field === 'quantity' ? parseFloat(value) || 0 : newItems[index].quantity;
      const rate = field === 'rate' ? parseFloat(value) || 0 : newItems[index].rate;
      newItems[index].amount = quantity * rate;
    }

    setLineItems(newItems);
  };

  const handleToggleEntry = (entryId) => {
    setSelectedEntries(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleSelectAll = () => {
    if (selectedEntries.length === unbilledEntries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(unbilledEntries.map(e => e.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedClient) {
      setError('Please select a client');
      return;
    }

    if (!dueDate) {
      setError('Please set a due date');
      return;
    }

    const client = clients.find(c => c.id === selectedClient);
    if (!client) {
      setError('Client not found');
      return;
    }

    setLoading(true);

    try {
      if (billingType === 'time-based') {
        // Time-based invoice
        if (selectedEntries.length === 0) {
          setError('Please select at least one time entry');
          setLoading(false);
          return;
        }

        await generateInvoice({
          clientId: selectedClient,
          timeEntryIds: selectedEntries,
          dueDate: new Date(dueDate),
          notes,
        });
      } else if (billingType === 'retainer') {
        // Retainer invoice with graduated discount
        if (!retainerInfo.baseRetainer || retainerInfo.baseRetainer <= 0) {
          setError('This client does not have a monthly retainer configured');
          setLoading(false);
          return;
        }

        await createRetainerInvoice(user.uid, {
          clientId: client.id,
          clientName: client.name,
          clientEmail: client.email,
          baseRetainer: retainerInfo.baseRetainer,
          graduatedDiscount: selectedClientData.graduatedDiscount,
          billingPeriod: retainerBillingPeriod,
          dueDate,
          currency: selectedClientData.currency || currency,
          notes,
        });
      } else {
        // Manual invoice with line items
        const validItems = lineItems.filter(item => item.description && item.amount > 0);

        if (validItems.length === 0) {
          setError('Please add at least one line item with a description and amount');
          setLoading(false);
          return;
        }

        await createManualInvoice(user.uid, {
          clientId: client.id,
          clientName: client.name,
          clientEmail: client.email,
          lineItems: validItems,
          dueDate,
          notes,
          currency,
          discountPercent: discountPercent || 0,
          discountAmount: manualDiscountAmount || 0,
        });
      }

      alert('Invoice created successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Invoice</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Billing Type Selection */}
            <div className="form-group">
              <label>Billing Type</label>
              <div className="billing-type-tabs">
                <button
                  type="button"
                  className={`billing-tab ${billingType === 'manual' ? 'active' : ''}`}
                  onClick={() => setBillingType('manual')}
                >
                  💰 Flat Fee
                </button>
                <button
                  type="button"
                  className={`billing-tab ${billingType === 'time-based' ? 'active' : ''}`}
                  onClick={() => setBillingType('time-based')}
                >
                  ⏱️ Time-Based
                </button>
                <button
                  type="button"
                  className={`billing-tab ${billingType === 'retainer' ? 'active' : ''}`}
                  onClick={() => setBillingType('retainer')}
                >
                  📅 Retainer
                </button>
              </div>
              <p className="help-text">
                {billingType === 'manual' && 'Create invoice with custom line items for project fees or fixed-price services'}
                {billingType === 'time-based' && 'Generate invoice from tracked time entries with hourly rates'}
                {billingType === 'retainer' && 'Generate monthly retainer invoice with graduated discount (if applicable)'}
              </p>
            </div>

            {/* Client Selection */}
            <div className="form-group">
              <label htmlFor="client">Select Client *</label>
              <select
                id="client"
                className="input"
                value={selectedClient}
                onChange={(e) => {
                  setSelectedClient(e.target.value);
                  setSelectedEntries([]);
                }}
                required
              >
                <option value="">Choose a client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Manual Billing - Line Items */}
            {billingType === 'manual' && (
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label>Line Items *</label>
                  <button
                    type="button"
                    className="btn-link"
                    onClick={handleAddLineItem}
                  >
                    + Add Line Item
                  </button>
                </div>

                <div className="line-items-container">
                  {/* Column Headers */}
                  <div className="line-item-row line-item-header">
                    <div className="line-item-fields">
                      <span>Description</span>
                      <span>Quantity</span>
                      <span>Rate</span>
                      <span>Amount</span>
                    </div>
                    <div style={{ width: '40px' }}></div>
                  </div>

                  {lineItems.map((item, index) => (
                    <div key={index} className="line-item-row">
                      <div className="line-item-fields">
                        <input
                          type="text"
                          className="input"
                          placeholder="Description (e.g., Website Design, Monthly Retainer)"
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        />
                        <input
                          type="number"
                          className="input input-small"
                          placeholder="Qty"
                          min="0"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        />
                        <input
                          type="number"
                          className="input input-small"
                          placeholder="Rate"
                          min="0"
                          step="0.01"
                          value={item.rate}
                          onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                        />
                        <input
                          type="number"
                          className="input input-small"
                          placeholder="Amount"
                          min="0"
                          step="0.01"
                          value={item.amount}
                          onChange={(e) => handleLineItemChange(index, 'amount', e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => handleRemoveLineItem(index)}
                        disabled={lineItems.length === 1}
                        title="Remove line item"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>

                <div className="invoice-summary">
                  <div className="summary-row">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(manualSubtotal, currency)}</span>
                  </div>
                  {discountPercent > 0 && (
                    <div className="summary-row" style={{ color: '#4caf50' }}>
                      <span>Discount ({discountPercent}%):</span>
                      <span>-{formatCurrency(manualDiscountAmount, currency)}</span>
                    </div>
                  )}
                  <div className="summary-row summary-total">
                    <span>Total:</span>
                    <span>{formatCurrency(manualTotal, currency)}</span>
                  </div>
                </div>

                {/* Client Billing Info */}
                {selectedClientData?.billingConfig && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: '#f0f4f8',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                  }}>
                    <div style={{ fontWeight: '500', marginBottom: '0.5rem', color: '#2c3e50' }}>
                      Client Billing Settings
                    </div>
                    {discountPercent > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ color: '#666' }}>Discount:</span>
                        <span>{discountPercent}%</span>
                      </div>
                    )}
                    {selectedClientData.billingConfig.invoiceCcEmails?.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ color: '#666' }}>CC on send:</span>
                        <span style={{ textAlign: 'right', fontSize: '0.8rem' }}>
                          {selectedClientData.billingConfig.invoiceCcEmails.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Time-Based Billing - Time Entries */}
            {billingType === 'time-based' && selectedClient && (
              <>
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label>Time Entries *</label>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={handleSelectAll}
                    >
                      {selectedEntries.length === unbilledEntries.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  
                  <div className="time-entries-list">
                    {unbilledEntries.length === 0 ? (
                      <p className="text-small text-secondary">No unbilled time entries for this client</p>
                    ) : (
                      unbilledEntries.map(entry => (
                        <label key={entry.id} className="entry-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedEntries.includes(entry.id)}
                            onChange={() => handleToggleEntry(entry.id)}
                          />
                          <div className="entry-details">
                            <div className="entry-info">
                              <span className="entry-date">{formatDate(entry.startTime)}</span>
                              <span className="entry-description">{entry.description || 'No description'}</span>
                            </div>
                            <div className="entry-amount">
                              <span className="entry-hours">{formatHours(entry.duration)} hrs</span>
                              <span className="entry-price">{formatCurrency(entry.amount)}</span>
                            </div>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {selectedEntries.length > 0 && (
                  <div className="invoice-summary">
                    <div className="summary-row">
                      <span>Total Hours:</span>
                      <span>{formatHours(timeTotalHours * 3600)}</span>
                    </div>
                    <div className="summary-row summary-total">
                      <span>Total Amount:</span>
                      <span>{formatCurrency(timeTotalAmount)}</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Retainer Billing */}
            {billingType === 'retainer' && selectedClient && (
              <div className="form-group">
                {/* Billing Period Selection */}
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="billingPeriod">Billing Period</label>
                  <input
                    id="billingPeriod"
                    type="month"
                    className="input"
                    value={retainerBillingPeriod}
                    onChange={(e) => setRetainerBillingPeriod(e.target.value)}
                  />
                  <p className="help-text">
                    {formatBillingPeriod(retainerBillingPeriod)}
                  </p>
                </div>

                {/* Retainer Info Display */}
                {retainerInfo.baseRetainer > 0 ? (
                  <div className="retainer-info-card" style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginTop: '1rem',
                  }}>
                    <h4 style={{ margin: '0 0 1rem', color: '#2c3e50' }}>
                      Retainer Details
                    </h4>

                    {/* Graduated Discount Info */}
                    {retainerInfo.discountInfo.enabled && (
                      <div style={{
                        background: '#e8f5e9',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        marginBottom: '1rem',
                        borderLeft: '4px solid #4caf50',
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#2e7d32', marginBottom: '0.25rem' }}>
                          Year {retainerInfo.discountInfo.currentYear} Loyalty Discount
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#555' }}>
                          {retainerInfo.discountInfo.discountPercent}% off base retainer
                        </div>
                      </div>
                    )}

                    {/* Discount Schedule Preview */}
                    {retainerInfo.discountInfo.enabled && retainerInfo.scheduleDisplay.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.5rem', color: '#666' }}>
                          Discount Schedule:
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {retainerInfo.scheduleDisplay.map((item) => (
                            <span
                              key={item.year}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                background: item.isCurrent ? '#4caf50' : '#e0e0e0',
                                color: item.isCurrent ? 'white' : '#666',
                              }}
                            >
                              {item.label}: {item.discountPercent}% off
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pricing Summary */}
                    <div className="invoice-summary" style={{ marginTop: '1rem' }}>
                      <div className="summary-row">
                        <span>Base Retainer:</span>
                        <span>{formatCurrency(retainerInfo.baseRetainer, selectedClientData?.currency || currency)}</span>
                      </div>
                      {retainerInfo.discountInfo.enabled && retainerInfo.discountSavings && (
                        <div className="summary-row" style={{ color: '#4caf50' }}>
                          <span>Discount ({retainerInfo.discountInfo.discountPercent}% off):</span>
                          <span>-{formatCurrency(retainerInfo.discountSavings.discountAmount, selectedClientData?.currency || currency)}</span>
                        </div>
                      )}
                      <div className="summary-row summary-total">
                        <span>Amount Due:</span>
                        <span style={{ fontSize: '1.2rem' }}>
                          {formatCurrency(retainerInfo.finalAmount, selectedClientData?.currency || currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    background: '#fff3e0',
                    padding: '1rem',
                    borderRadius: '8px',
                    borderLeft: '4px solid #ff9800',
                  }}>
                    <p style={{ margin: 0, color: '#e65100' }}>
                      This client does not have a monthly retainer configured.
                      Please set up retainer pricing in the client settings first.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Common Fields */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dueDate">Due Date *</label>
                <input
                  id="dueDate"
                  type="date"
                  className="input"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                />
              </div>

              {billingType === 'manual' && (
                <div className="form-group">
                  <label htmlFor="currency">Currency</label>
                  <select
                    id="currency"
                    className="input"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="PHP">PHP - Philippine Peso</option>
                    <option value="SGD">SGD - Singapore Dollar</option>
                  </select>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes / Payment Instructions</label>
              <textarea
                id="notes"
                className="input"
                rows="3"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes, terms, or payment instructions..."
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
